import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type SubagentInfo, useAppStore } from '../stores/appStore';
import { useGateway } from '../hooks/useGateway';
import MarkdownRenderer from './MarkdownRenderer';

// ============================================================
// SubagentPill - Individual pill showing subagent status
// ============================================================

/** Duration of the fade-out animation in ms (matches CSS) */
const FADE_OUT_DURATION_MS = 2500;

const SUBAGENT_TRIM_PREFIX = '…\n\n';

function normalizeTrimmedSubagentMarkdown(text: string): string {
  if (!text) {
    return text;
  }

  const trimmedPrefix = text.startsWith(SUBAGENT_TRIM_PREFIX);
  const contentWithoutPrefix = trimmedPrefix ? text.slice(SUBAGENT_TRIM_PREFIX.length) : text;
  const normalizedForFenceCheck = contentWithoutPrefix.replace(/^\n+/, '');
  const fenceCount = (normalizedForFenceCheck.match(/(^|\n)```/g) ?? []).length;

  if (fenceCount % 2 === 1 && !normalizedForFenceCheck.startsWith('```')) {
    return `${trimmedPrefix ? SUBAGENT_TRIM_PREFIX : ''}\`\`\`\n${contentWithoutPrefix}`;
  }

  return text;
}

interface SubagentPillProps {
  info: SubagentInfo;
  onClick: () => void;
  onDismiss?: () => void;
}

function SubagentPill({ info, onClick, onDismiss }: SubagentPillProps) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const removeSubagent = useAppStore((state) => state.removeSubagent);

  // Update elapsed time every second for running subagents
  useEffect(() => {
    if (info.status !== 'running') {
      // For completed subagents, show final elapsed time
      const finalTime = info.completedAt
        ? Math.floor((info.completedAt - info.startTime) / 1000)
        : Math.floor((Date.now() - info.startTime) / 1000);
      setElapsedSeconds(finalTime);
      return;
    }

    // Running: update every second
    const updateElapsed = () => {
      setElapsedSeconds(Math.floor((Date.now() - info.startTime) / 1000));
    };
    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [info.status, info.startTime, info.completedAt]);

  // Remove from store after fade-out animation completes
  useEffect(() => {
    if (!info.fadingOut) return;
    const timer = setTimeout(() => {
      removeSubagent(info.sessionKey);
    }, FADE_OUT_DURATION_MS);
    return () => clearTimeout(timer);
  }, [info.fadingOut, info.sessionKey, removeSubagent]);

  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const fadeClass = info.fadingOut ? 'subagent-fade-out' : '';

  const pillStyle = info.status === 'running'
    ? { background: 'rgba(255, 123, 53, 0.07)', border: '1px solid rgba(255, 123, 53, 0.25)', color: '#FF7B35' }
    : info.status === 'completed'
      ? { background: 'rgba(61, 255, 168, 0.07)', border: '1px solid rgba(61, 255, 168, 0.2)', color: '#3DFFA8' }
      : { background: 'rgba(255, 70, 70, 0.08)', border: '1px solid rgba(255, 70, 70, 0.25)', color: '#fca5a5' };

  return (
    <div className={`flex items-center ${fadeClass}`}>
      <button
        type="button"
        onClick={onClick}
        className={`flex items-center gap-2 rounded-full px-3 py-1.5 transition-all duration-200 ${!info.fadingOut ? 'animate-in fade-in slide-in-from-bottom-2' : ''}`}
        style={{ ...pillStyle, fontFamily: 'var(--app-ui-font)', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', transition: 'all 0.2s ease' }}
      >
        {/* Status indicator */}
        {info.status === 'running' ? (
          <span className="subagent-pulse-dot h-1.5 w-1.5 rounded-full" style={{ background: '#FF7B35' }} />
        ) : info.status === 'completed' ? (
          <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" style={{ color: '#3DFFA8' }}>
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        ) : (
          <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" style={{ color: '#fca5a5' }}>
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        )}

        {/* Label */}
        <span>{info.label}</span>

        {/* Elapsed time */}
        <span style={{ opacity: 0.65 }}>{formatTime(elapsedSeconds)}</span>
      </button>

      {/* Explicit dismiss button for error pills */}
      {info.status === 'error' && !info.fadingOut && onDismiss && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          className="ml-1 rounded-full p-1 text-muted-foreground transition-colors"
          style={{ transition: 'all 0.2s ease' }}
          title="Dismiss error"
          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,70,70,0.12)')}
          onMouseLeave={e => (e.currentTarget.style.background = '')}
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}
    </div>
  );
}

// ============================================================
// SubagentPopup - Panel showing subagent details (non-modal)
// ============================================================

interface SubagentPopupProps {
  info: SubagentInfo;
  onClose: () => void;
}

function SubagentPopup({ info, onClose }: SubagentPopupProps) {
  const { loadSubagentHistory, killSubagentSession } = useGateway();
  const removeSubagent = useAppStore((state) => state.removeSubagent);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [killing, setKilling] = useState(false);

  useEffect(() => {
    setTranscript(null);
    setLoadingTranscript(false);
  }, [info.sessionKey]);

  // Load transcript for completed subagents without streaming text
  useEffect(() => {
    if (info.status === 'running' || info.streamingText || transcript || loadingTranscript) {
      return;
    }

    let cancelled = false;
    setLoadingTranscript(true);

    void loadSubagentHistory(info.sessionKey)
      .then((result) => {
        if (!cancelled) {
          setTranscript(result);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingTranscript(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [info.status, info.streamingText, info.sessionKey, transcript, loadingTranscript, loadSubagentHistory]);

  const killDisabled = info.status !== 'running' || killing;

  const handleKill = async () => {
    if (killDisabled) {
      return;
    }

    setKilling(true);
    const killed = await killSubagentSession(info.sessionKey);
    setKilling(false);

    if (killed) {
      onClose();
      removeSubagent(info.sessionKey);
    }
  };

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Auto-scroll to bottom on content changes
  const scrollRef = useRef<HTMLDivElement>(null);
  const displayText = info.streamingText || transcript || '';
  const preparedDisplayText = useMemo(
    () => normalizeTrimmedSubagentMarkdown(displayText),
    [displayText],
  );

  useEffect(() => {
    if (scrollRef.current && info.status === 'running') {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [preparedDisplayText, info.status]);

  const statusLabel =
    info.status === 'running'
      ? 'Running'
      : info.status === 'completed'
        ? 'Completed'
        : 'Error';

  const statusColor =
    info.status === 'running'
      ? 'text-primary'
      : info.status === 'completed'
        ? 'text-green-500'
        : 'text-destructive';

  return (
    <div
      className="absolute bottom-full left-0 right-0 z-40 mb-2 px-4 animate-in fade-in slide-in-from-bottom-2 duration-200"
    >
      <div className="mx-auto flex max-h-[70vh] w-[95vw] max-w-[900px] flex-col rounded-xl shadow-xl" style={{ background: '#161929', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 0 50px rgba(255, 123, 53, 0.08), 0 24px 64px rgba(0,0,0,0.4)' }}>
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-3">
            <span style={{ fontFamily: 'var(--app-heading-font)', fontWeight: 700, fontSize: '1rem', color: 'var(--theme-foreground)' }}>{info.label}</span>
            <span className={`text-sm ${statusColor}`} style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{statusLabel}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleKill}
              disabled={killDisabled}
              title={
                info.status === 'running'
                  ? 'Kill subagent'
                  : 'Kill unavailable after the subagent has finished'
              }
              className="inline-flex items-center gap-1.5 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-1.5 text-xs font-medium text-destructive transition-colors hover:bg-destructive/20 disabled:cursor-not-allowed disabled:border-border disabled:bg-muted disabled:text-muted-foreground disabled:hover:bg-muted"
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-10.293a1 1 0 00-1.414-1.414L10 8.586 7.707 6.293a1 1 0 00-1.414 1.414L8.586 10l-2.293 2.293a1 1 0 101.414 1.414L10 11.414l2.293 2.293a1 1 0 001.414-1.414L11.414 10l2.293-2.293z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{killing ? 'Killing…' : 'Kill'}</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-muted-foreground hover:bg-card hover:text-foreground"
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Body - scrollable output with markdown rendering */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-4"
          style={{ minHeight: '120px' }}
        >
          {loadingTranscript ? (
            <div className="flex items-center justify-center py-8">
              <span className="text-muted-foreground">Loading transcript...</span>
            </div>
          ) : preparedDisplayText ? (
            <div
              className="subagent-popup-bubble max-w-full rounded-2xl px-4 py-3 text-sm"
              style={{
                backgroundColor: 'var(--theme-assistant-bubble-background)',
                color: 'var(--theme-assistant-bubble-text)',
              }}
            >
              <MarkdownRenderer
                content={preparedDisplayText}
                isStreaming={info.status === 'running'}
                suppressRawFallback
              />
              {info.status === 'running' && <span className="streaming-dot" />}
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <span className="text-muted-foreground">No output captured</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// SubagentBar - Container showing pills for active subagents
// ============================================================

export default function SubagentBar() {
  const subagentRuns = useAppStore((state) => state.subagentRuns);
  const pillBarSubagentsFn = useAppStore((state) => state.pillBarSubagents);
  const acknowledgeSubagent = useAppStore((state) => state.acknowledgeSubagent);
  const clearSubagentAutoOpen = useAppStore((state) => state.clearSubagentAutoOpen);
  const startSubagentFadeOut = useAppStore((state) => state.startSubagentFadeOut);
  const userRole = useAppStore((state) => state.userRole);
  const [openPopupSessionKey, setOpenPopupSessionKey] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Get subagents to display in pill bar
  const pillSubagents = pillBarSubagentsFn();

  // Find the currently open subagent info
  const openSubagent = openPopupSessionKey ? subagentRuns[openPopupSessionKey] : null;

  // Handle popup close — acknowledge completed subagents, start fade-out
  const handlePopupClose = useCallback(() => {
    if (openPopupSessionKey) {
      const info = subagentRuns[openPopupSessionKey];
      if (info && info.status === 'completed' && !info.acknowledged) {
        // Mark as acknowledged and start fade-out
        acknowledgeSubagent(openPopupSessionKey);
        startSubagentFadeOut(openPopupSessionKey);
      }
    }
    setOpenPopupSessionKey(null);
  }, [openPopupSessionKey, subagentRuns, acknowledgeSubagent, startSubagentFadeOut]);

  // Handle explicit dismiss for error pills
  const handleDismiss = useCallback((sessionKey: string) => {
    startSubagentFadeOut(sessionKey);
  }, [startSubagentFadeOut]);

  // Auto-open /btw popups once streaming text starts
  useEffect(() => {
    const subagentToAutoOpen = pillSubagents.find(
      (info) => info.autoOpenOnStreaming && info.status !== 'error' && info.streamingText.trim().length > 0,
    );

    if (!subagentToAutoOpen) {
      return;
    }

    setOpenPopupSessionKey(subagentToAutoOpen.sessionKey);
    clearSubagentAutoOpen(subagentToAutoOpen.sessionKey);
  }, [pillSubagents, clearSubagentAutoOpen]);

  // Close popup when clicking outside the subagent bar / popup area
  useEffect(() => {
    if (!openPopupSessionKey) {
      return;
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (containerRef.current && target && !containerRef.current.contains(target)) {
        handlePopupClose();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [openPopupSessionKey, handlePopupClose]);

  // Don't render if no subagents to show, or for invited users (hide builder-only UI)
  if (userRole === 'member' || pillSubagents.length === 0) {
    return null;
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Popup panel — positioned above the pill bar */}
      {openSubagent && (
        <SubagentPopup
          key={openSubagent.sessionKey}
          info={openSubagent}
          onClose={handlePopupClose}
        />
      )}

      {/* Pill bar */}
      <div className="flex flex-wrap gap-2 px-4 py-2">
        {pillSubagents.map((info: SubagentInfo) => (
          <SubagentPill
            key={info.sessionKey}
            info={info}
            onClick={() => setOpenPopupSessionKey(info.sessionKey)}
            onDismiss={() => handleDismiss(info.sessionKey)}
          />
        ))}
      </div>
    </div>
  );
}
